import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "mobile-games",
  app: "Mobile games",
  title: "How to block mobile games on iPhone — and the daily reward",
  h1: "How to block mobile games",
  description:
    "Screen Time steps for games as a category, why the daily login bonus is the real hook, and how to leave without losing a year of progress you paid for.",
  targets: [
    "how to block games on iphone",
    "stop playing mobile games",
    "mobile game addiction help",
    "how to limit games screen time",
  ],
  published: "2026-09-03",
  standfirst:
    "The reason you open it every day is not that it is fun every day. It is that something in there decays if you do not.",
  takeaways: [
    "Limit the Games category, not one game. Uninstalling one and installing another is the most common outcome of a single-app block.",
    "Daily rewards, energy timers and season passes are all the same mechanic: a cost for not turning up.",
    "The progress is real to you and worthless to anyone else, and admitting that is usually the step that makes leaving possible.",
  ],
  mechanic: [
    "Free-to-play mobile games are not designed to be played. They are designed to be attended, which is a different thing. The daily login bonus, the energy bar that refills over hours, the timed event, the streak on a season pass — every one of these is a mechanism that punishes absence rather than rewarding presence, and the difference matters because it means the pull continues on days you do not want to play.",
    "That is why mobile games produce a pattern nobody reports from console or PC gaming: opening the app for ninety seconds, collecting something, and closing it, several times a day, without enjoyment. It is maintenance. The game has been designed to become a small obligation, and obligations do not require you to be having a good time.",
    "The second mechanic is sunk cost, and it is stronger here than anywhere else on this list. A year of progress, possibly some money, and often a clan or guild who notice if you stop — the same structure Snapchat's streaks use, wrapped around something you have also invested in. Leaving is not deleting an app; it is writing off an asset.",
  ],
  limitFails:
    "A limit on one game is the least effective intervention on this site: the category is enormous, the alternatives are free, and the app store is one tap away, so the habit reinstalls itself under a different name within a week. The other failure is specific to the daily-reward design — a limit that permits thirty minutes still permits the ninety-second maintenance visits, which are the part that keeps the hook set, so the limit can be respected perfectly while the thing it was set for continues unaffected.",
  instead: [
    "Limit the whole Games category through Screen Time, not the individual title.",
    "Turn off all game notifications. The energy-refilled and event-starting alerts are the entire summoning system.",
    "Delete the account, not just the app, where the game allows it. Keeping the account is keeping the option, and the option is what you reinstall against.",
    "If there is a clan or guild, tell them. The social obligation is real and leaving without a word makes it heavier rather than lighter.",
  ],
  verses: [
    {
      verse: {
        text: '"All things are lawful for me," but not all things are expedient. "All things are lawful for me," but I will not be brought under the power of anything.',
        ref: "1 Corinthians 6:12",
      },
      note: "The question Paul asks is about power rather than permission, which is the only useful one here. A game is not a moral issue. Opening one every morning to prevent a number decaying, on days you do not want to play, is a fairly exact description of being brought under the power of something.",
    },
    {
      verse: {
        text: "Like a city that is broken down and without walls is a man whose spirit is without restraint.",
        ref: "Proverbs 25:28",
      },
      note: "An image about defence rather than about morals. A city without walls is not wicked; it is available — whatever passes can walk in and take what it wants. Applied to a phone with no category limit and notifications on, the proverb is not being stretched much at all.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Category, not title",
      id: "category-not-title",
    },
    {
      type: "p",
      text: "This is the one place on the site where the category limit is not merely better but necessary. The supply is effectively infinite and free, so a block on one game is a recommendation to find another.",
    },
    {
      type: "p",
      text: "Apple's Games category covers essentially all of them, and setting a limit there is the same four taps as setting one on a single app.",
    },
    {
      type: "h2",
      text: "Punishing absence",
      id: "punishing-absence",
    },
    {
      type: "p",
      text: "Once you can see the daily bonus for what it is, most of the compulsion becomes legible. It is not a gift for playing; it is a penalty for not, arranged so the penalty is felt rather than stated.",
    },
    {
      type: "note",
      text: "The test: on a day you did not want to play at all, did you open it anyway to collect something? That is the mechanic working exactly as designed.",
    },
    {
      type: "h2",
      text: "The progress is not an asset",
      id: "the-progress-is-not-an-asset",
    },
    {
      type: "p",
      text: "Sunk cost is doing more work here than enjoyment is. A year of levels feels like something you own, and it is worth nothing to anybody, cannot be sold, and evaporates the day the servers close — which for mobile games is frequently sooner than players expect.",
    },
    {
      type: "p",
      text: "Saying that plainly is usually what makes leaving possible, and it is the same reckoning {every one of these apps} eventually asks for.",
      links: {
        "every one of these apps": "/block",
      },
    },
  ],
  faq: [
    {
      q: "How do I block games on my iPhone?",
      a: "Settings → Screen Time → App Limits → Add Limit → Games, with Block at End of Limit and a passcode. Limiting the category rather than one title is essential here, because the alternatives are free and endless.",
    },
    {
      q: "Why do I open a game I do not even enjoy?",
      a: "Because the daily reward, energy timer and season pass all punish absence rather than reward presence. The ninety-second maintenance visit is the design working, not a failure of yours.",
    },
    {
      q: "How do I quit a game I have spent money on?",
      a: "Recognise that the progress is not an asset — it cannot be sold, cannot be moved, and disappears when the servers do. Then delete the account rather than the app, and tell the guild if there is one.",
    },
  ],
  relatedApps: ["tiktok", "discord"],
  related: ["dopamine-detox-christian", "screen-time-limits-dont-work"],
  relatedPrayers: [
    "prayer-for-self-control",
    "prayer-before-you-open-your-phone",
  ],
};
